/**
 * The small, platform-clean P2P integration seam that `sendFile.ts` /
 * `receiveHost.ts` need for their OPTIONAL `deps.p2p` promotion hook
 * (docs/file-transfer-p2p.md §16 pt.4). Extracted (TASK-27 M4 extraction)
 * from `apps/extension/src/sidepanel/filetransfer/p2pSenderAttempt.ts` /
 * `apps/extension/src/offscreen/filetransfer/p2pReceiverAttempt.ts` — these
 * are genuine pure deps of the moved sender/receiver cores (both a runtime
 * dependency via `mapDemoteToSkipBelowPart` and a type dependency via the
 * `deps.p2p?` field), so they move here too.
 *
 * Deliberately NOT moved: the heavy `RTCPeerConnection`-touching attempt
 * drivers themselves (`p2pTransport.ts`, `p2pFraming.ts`,
 * `p2pSenderAttempt.ts`, `p2pReceiverAttempt.ts`, `p2pCtrlBus.ts`) — P2P
 * wiring is out of scope for M4 web (relay-only, "do NOT wire P2P"; P2P on
 * web is a flagged fast-follow). Those stay extension-only; the extension's
 * `p2pSenderAttempt.ts`/`p2pReceiverAttempt.ts` now import the types/util
 * below from here instead of defining them locally. A web (or any future)
 * surface that never sets `deps.p2p` gets byte-identical M2 relay-only
 * behavior — every consumer of these types already treats `p2p` as optional.
 */

import type { InterruptSignal } from "./activeSendControl";
import type { ReceiveHostCrypto, ReceiveHostHash } from "./receiveHost";
import type { ReceiveResumeDeps } from "./receiveResume";
import type { SenderResumeDeps } from "./senderResume";
import type { SenderCrypto, SenderSource } from "./sendFile";
import type { FtReceiverStateRecord } from "./state";

/**
 * Why a P2P attempt fell back to relay. Mirrors the `trigger` enum in
 * `packages/core/analytics/events.json` and the FtLog `demote` labels; these are
 * stable analytics wire values, so treat them as append-only.
 *
 * `connect_failed` is included even though it is NOT a demotion in the protocol
 * sense (no channel ever opened, so no `demote` ctrl is sent). Without it, "P2P
 * never connected" and "P2P connected then degraded" are indistinguishable
 * downstream — and once the relay fallback works, both simply look like relay
 * traffic. It does NOT arrive via a `demoted` outcome: it is raised by the hosts
 * from `{ kind: "not-attempted", reason: "connect_failed" }`, which is how a
 * surface distinguishes "the channel died" from "we never tried".
 */
export type P2pDemoteTrigger =
  | "T2"
  | "T3"
  | "T4"
  | "T5"
  | "T6"
  | "early"
  | "local_error"
  | "connect_failed";

/**
 * Which path actually carried the bytes, as reported at a terminal.
 *
 * Threaded through the terminal signal rather than read from a surface's
 * "is P2P active" registry, because every surface CLEARS that registry before
 * its terminal branches run — by the time anything terminal fires, the
 * transport is no longer reconstructable.
 *
 * `relay_after_demote` is kept distinct from `relay` on purpose: "P2P was never
 * tried" and "P2P was tried and fell back" are the same byte path but opposite
 * signals about P2P health, and conflating them is precisely what the relay
 * fallback would otherwise hide.
 */
export type FtTransport = "p2p" | "relay" | "relay_after_demote";

/**
 * Why a P2P attempt was never made. `"connect_failed"` is the ONLY value that means
 * "we tried and the channel never opened" — the absence of a reason means we never
 * tried (no `deps.p2p`, feature off), which is the DEFAULT outcome the hosts
 * initialize, never one the attempt functions return. The distinction matters: firing a
 * `connect_failed` demote for a transfer that never attempted P2P would inflate the
 * failure rate with traffic that was always going to be relay.
 *
 * Producers live in the surfaces (extension `p2pSenderAttempt`/`p2pReceiverAttempt`,
 * Android coordinators) — they own the `connect() -> null` branch and set this there.
 */
export type P2pNotAttemptedReason = "connect_failed";

// --- Sender side (docs/file-transfer-p2p.md §16 pt.4) ---

/** One sender-side P2P attempt's outcome. */
export type P2pSendOutcome =
  | { kind: "completed" }
  | { kind: "demoted"; lastVerifiedChunk: number; trigger: P2pDemoteTrigger }
  | { kind: "not-attempted"; reason?: P2pNotAttemptedReason };

/**
 * The business-data contract `SenderDeps.p2p.attempt` exposes to `sendFile.ts` (pure core) —
 * no ctrl-bus/session/timer specifics leak across this boundary.
 *
 * SINGLE-FILE ONLY: `sendFile.ts` gates P2P on `files.length === 1`, which is why these args
 * carry one `fileId`/`chunkCount`/`source` rather than a `partPlan`. That gate is also what
 * makes the chunk index unambiguous for the AEAD nonce — with one file the file-aligned GLOBAL
 * index equals the local one (crypto-spec.md §6a). Whoever lifts the gate MUST thread
 * `globalChunkOffsets` through the per-surface P2P loops at the same time.
 */
export interface P2pSendAttemptArgs {
  transferId: string;
  fileId: string;
  chunkCount: number;
  source: SenderSource;
  crypto: Pick<SenderCrypto, "encryptChunk">;
  transferKey: Uint8Array;
  noncePrefix: Uint8Array;
  checkInterrupt?: () => InterruptSignal | undefined;
  /**
   * TASK-30 — fired exactly once, the moment the attempt's data channel opens and the send loop
   * begins (the CONNECTING_P2P -> TRANSFERRING_P2P transition, docs/file-transfer-p2p.md's state
   * machine). The only consumer is the UI-facing "direct" transport badge: `sendFile.ts` uses this
   * to emit ONE `{stage:"uploading", transport:"p2p"}` progress event so the card can show the
   * badge for the duration of the P2P leg — the badge then clears naturally the next time a
   * `notify()` call omits `transport` (relay fallback after a demotion, or any terminal stage).
   * Deliberately NOT a per-chunk/per-part callback — P2P's own send loop (`runP2pSendLoop`) doesn't
   * report granular progress to the UI (out of scope here; "no new transfer logic"), only that the
   * transport switched.
   */
  onTransferring?: () => void;
  /**
   * Fired after each chunk frame is handed to the DataChannel (post-backpressure), with the
   * cumulative count. Hardware-demo follow-up to `onTransferring`: without it, the send card's
   * part counter freezes at "part 1" for the whole P2P transfer and jumps straight to Sent.
   * Callers throttle to part boundaries themselves — this fires per chunk.
   */
  onChunkProgress?: (chunksSent: number, chunkCount: number) => void;
}

/** `SenderDeps.p2p` — real wiring in the extension's `senderDeps.ext.ts`. */
export interface SenderP2pDeps {
  /** Acquire `transferId`'s single-writer Web Lock BEFORE attempting P2P — held across the whole attempt-then-fallback window (§16 pt.4). */
  acquireLock(transferId: string): Promise<boolean>;
  releaseLock(transferId: string): Promise<void>;
  attempt(args: P2pSendAttemptArgs): Promise<P2pSendOutcome>;
  /** `SenderResumeDeps` for the SAME transfer, so a demotion can call `senderResume.ts`'s exported `reconcileAndContinue` directly inside the already-held lock. */
  resumeDeps: SenderResumeDeps;
  /** §9.3's `demotedFromP2p` early-complete seam: subscribe to a wire `complete{verified:true}` ctrl for `transferId`, for the duration of a post-demotion relay reconcile only. */
  subscribeWireComplete(transferId: string, handler: () => void): () => void;
  /** §9.3: request a pause on `transferId`'s active control-registry entry — used ONLY to abort a post-demotion relay reconcile early on a late-arriving verified complete ctrl. */
  requestPause(transferId: string): void;
  /**
   * Observability seam, invoked EXACTLY ONCE where the demoted outcome is
   * consumed — never at the raw `ftLog("demote")` sites, which fire more than
   * once per attempt (a post-resolve `channel.close()` re-enters `onClose` and
   * emits a second, mislabelled T2; and the receiver's `ChunkFrameReader` has no
   * latched error state, so one desync raises T4 on every subsequent inbound
   * frame). The outcome is the only once-per-attempt structure.
   *
   * Analytics-only and MUST fail open: client-core imports no analytics SDK, so
   * the host supplies this. A throw here would abort a fallback that is
   * otherwise about to succeed.
   */
  onDemote?(info: {
    trigger: P2pDemoteTrigger;
    lastVerifiedChunk: number;
    chunkCount: number;
  }): void;
}

/**
 * §8.3 step 3's demote mapping (pinned formula) — exported standalone so both
 * `sendFile.ts` and the extension's own `p2pSenderAttempt.ts` tests can
 * exercise it directly without driving a full attempt.
 */
export function mapDemoteToSkipBelowPart(
  lastVerifiedChunk: number,
  chunkCount: number,
  partCount: number,
  chunksPerPart: number
): number {
  const lvcClamped = Math.min(Math.max(0, Math.floor(lastVerifiedChunk)), chunkCount);
  return lvcClamped === chunkCount ? partCount : Math.floor(lvcClamped / chunksPerPart);
}

// --- Receiver side ---

/**
 * One receiver-side P2P attempt's outcome. `"completed"` carries
 * `closeChannel` rather than closing the channel itself: §9.1's pinned
 * ordering is COMPLETE frame -> wire `complete` ctrl -> P-R4 persist -> THEN
 * close DC/PC/session — `receiveHost.ts`'s existing tail owns the ctrl+
 * persist, so it also owns invoking this AFTER both have run.
 */
export type P2pReceiveOutcome =
  | { kind: "completed"; closeChannel(): void }
  | { kind: "hashMismatch" }
  | { kind: "demoted"; lastVerifiedChunk: number; trigger: P2pDemoteTrigger }
  | { kind: "not-attempted"; reason?: P2pNotAttemptedReason };

/**
 * The business-data contract `ReceiveHostDeps.p2p.attempt` exposes to `receiveHost.ts` (pure core).
 * SINGLE-FILE ONLY — see `P2pSendAttemptArgs`' doc for the gate and its nonce-index consequence.
 */
export interface P2pReceiveAttemptArgs {
  transferId: string;
  fileId: string;
  chunkCount: number;
  chunkSize: number;
  fileSize: number;
  /** Case-insensitive hex — compared against the finalized running digest (§9.1). */
  expectedSha256: string;
  output: { writeAt(offset: number, data: Uint8Array): Promise<void>; flush(): Promise<void> };
  crypto: Pick<ReceiveHostCrypto, "decryptChunk">;
  hash: Pick<ReceiveHostHash, "update" | "final">;
  hashState: unknown;
  transferKey: Uint8Array;
  noncePrefix: Uint8Array;
  /** P-R2 persist seam (§5.3 step 5) — the SAME seam the relay download loop uses. */
  onPartWritten?(partIndex: number, chunksVerified: number): Promise<void>;
  isAborted?(): boolean;
  /** TASK-30 — receiver-side counterpart of `P2pSendAttemptArgs.onTransferring`; see its doc. */
  onTransferring?(): void;
}

/** `ReceiveHostDeps.p2p` — real wiring in the extension's `receiveDeps.ext.ts`. */
export interface ReceiverP2pDeps {
  /** Acquire `transferId`'s single-writer Web Lock right after `output.open()` (before the accept ctrl) — held for the fresh receive's whole lifetime (§16 pt.4). */
  acquireLock(transferId: string): Promise<boolean>;
  releaseLock(transferId: string): Promise<void>;
  attempt(args: P2pReceiveAttemptArgs): Promise<P2pReceiveOutcome>;
  /** Fetch the CURRENT persisted receiver record for the demote reconcile handoff. */
  getRecord(): Promise<FtReceiverStateRecord>;
  /** `ReceiveResumeDeps` for the SAME transfer, so a demotion can call `receiveResume.ts`'s exported `reconcileAndContinue` directly inside the already-held lock/host bookkeeping. */
  resumeDeps: ReceiveResumeDeps;
  /**
   * Observability seam, invoked EXACTLY ONCE where the demoted outcome is
   * consumed — never at the raw `ftLog("demote")` sites, which fire more than
   * once per attempt (a post-resolve `channel.close()` re-enters `onClose` and
   * emits a second, mislabelled T2; and the receiver's `ChunkFrameReader` has no
   * latched error state, so one desync raises T4 on every subsequent inbound
   * frame). The outcome is the only once-per-attempt structure.
   *
   * Analytics-only and MUST fail open: client-core imports no analytics SDK, so
   * the host supplies this. A throw here would abort a fallback that is
   * otherwise about to succeed.
   */
  onDemote?(info: {
    trigger: P2pDemoteTrigger;
    lastVerifiedChunk: number;
    chunkCount: number;
  }): void;
}
